// SpendEditModal.tsx — add or change the amount spent on one sync (ADMIN ONLY).
// Changing an existing amount needs a reason; the API keeps every change in
// the spend's history.

import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Check, IndianRupee, Loader2, X } from 'lucide-react';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import SpendFields, { SpendFormState, checkSpendForm, emptySpendForm, formFromSpend } from './SpendFields';
import { SpendForSync, SyncSpend, spendApi, spendError } from './spendTypes';
import { Banner, TONES } from '../../SheetsManager/parts/SheetImportDetailsModal';

const REASON_MAX = 300;

interface SpendEditModalProps {
  visible: boolean;
  importId: string | null;
  /** What GET /spend returned (amount + campaigns). */
  current: SpendForSync | null;
  sheetName?: string;
  onHide: () => void;
  onSaved: (s: SyncSpend) => void;
}

const SpendEditModal: React.FC<SpendEditModalProps> = ({ visible, importId, current, sheetName, onHide, onSaved }) => {
  const [form, setForm] = useState<SpendFormState>(emptySpendForm());
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const existing = current?.data ?? null;
  const campaigns = current?.campaigns ?? [];

  useEffect(() => {
    if (!visible) return;
    setForm(existing ? formFromSpend(existing) : emptySpendForm());
    setReason('');
    setError(null);
  }, [visible, existing]);

  const check = checkSpendForm(form, campaigns);
  const needsReason = !!existing;
  const canSave = !!check.input && (!needsReason || reason.trim().length > 0) && !saving;

  const handleHide = () => {
    if (!saving) onHide();
  };

  const save = async () => {
    if (!importId || !check.input) return;
    setSaving(true);
    setError(null);
    try {
      const saved = await spendApi.set(importId, { ...check.input, reason: reason.trim() || undefined });
      onSaved(saved);
      onHide();
    } catch (e: any) {
      setError(spendError(e, 'Could not save the amount.'));
    } finally {
      setSaving(false);
    }
  };

  const header = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          flexShrink: 0,
          background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
          color: C.lime,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 6px 18px rgba(17,85,80,0.25)',
        }}
      >
        <IndianRupee size={19} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '1.01rem', fontWeight: 700, color: '#111827', lineHeight: 1.2, marginBottom: '3px' }}>
          {existing ? 'Change amount spent' : 'Add amount spent'}
        </div>
        <div style={{ fontSize: '0.71rem', color: C.sub, fontWeight: 500 }}>
          {sheetName ? <strong style={{ color: C.teal }}>{sheetName}</strong> : 'This sync'} · only admins see this
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={handleHide}
      header={header}
      className="sem-dialog"
      maskClassName="sem-mask"
      style={{ width: '92vw', maxWidth: '520px' }}
      modal
      closable={!saving}
      dismissableMask={!saving}
      draggable={false}
      resizable={false}
      blockScroll
    >
      <style>{`
        .sem-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; }
        .sem-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; }
        .sem-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.soft}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
        .sem-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; max-height: 76vh; overflow-y: auto; }
        .sem-spin { animation: semSpin 0.9s linear infinite; }
        @keyframes semSpin { to { transform: rotate(360deg); } }
      `}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
        <div style={{ background: G.shellBg, border: `1px solid ${G.shellBorder}`, borderRadius: '13px', padding: '4.6px' }}>
          <div style={{ background: C.white, border: `1px solid ${G.line}`, borderRadius: '9.2px', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <SpendFields
              value={form}
              onChange={setForm}
              campaigns={campaigns}
              leadsCount={current?.leads_count ?? 0}
              disabled={saving}
              autoFocus
            />

            {needsReason && (
              <div>
                <label
                  htmlFor="sem-reason"
                  style={{ display: 'block', fontSize: '0.64rem', fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '5px' }}
                >
                  Why are you changing it? <span style={{ color: '#c62828' }}>*</span>
                </label>
                <textarea
                  id="sem-reason"
                  value={reason}
                  maxLength={REASON_MAX}
                  disabled={saving}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Meta invoice came in higher than the estimate"
                  rows={2}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    resize: 'vertical',
                    padding: '8px 10px',
                    border: `1px solid ${G.line}`,
                    borderRadius: '8.3px',
                    fontSize: '0.74rem',
                    fontFamily: 'inherit',
                    color: '#111827',
                    outline: 'none',
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: C.muted, marginTop: '3px' }}>
                  <span>Saved in this sync’s change history.</span>
                  <span>
                    {reason.length}/{REASON_MAX}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {error && (
          <Banner tone={TONES.red} icon={<X size={15} />}>
            {error}
          </Banner>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '7.4px' }}>
          <GlobalButton label="Cancel" variant="secondary" icon={X} onClick={handleHide} disabled={saving} />
          <button
            type="button"
            onClick={save}
            disabled={!canSave}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '0 18px',
              height: '36px',
              borderRadius: '8px',
              border: 'none',
              background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
              color: '#fff',
              fontSize: '0.72rem',
              fontWeight: 800,
              cursor: saving ? 'wait' : canSave ? 'pointer' : 'not-allowed',
              opacity: canSave || saving ? 1 : 0.5,
            }}
          >
            {saving ? <Loader2 size={14} className="sem-spin" /> : <Check size={14} strokeWidth={3} />}
            {saving ? 'SAVING…' : 'SAVE AMOUNT'}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default SpendEditModal;
